import React, { Component, Fragment } from "react";
import { observer, inject } from "mobx-react";
import moment from "moment";
import {
    IconButton,
    Avatar,
    Tooltip,
    Button,
    TextField,
    CircularProgress,
    Popover
} from "@material-ui/core";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import EditIcon from "@material-ui/icons/Edit";
import DeleteIcon from "@material-ui/icons/Delete";
import SendIcon from "@material-ui/icons/Send";
import InsertEmoticonIcon from "@material-ui/icons/InsertEmoticon";
import AttachFileIcon from "@material-ui/icons/AttachFile";
import CloseIcon from "@material-ui/icons/Close";
import GetAppIcon from "@material-ui/icons/GetApp";
import InsertDriveFileIcon from "@material-ui/icons/InsertDriveFile";
import ImageIcon from "@material-ui/icons/Image";
import { ProfileType } from "../../../types/enum";
import ProfileSearchBox from "../../fields/ProfileSearchBox";
import ConfirmationModal from "../../modal/ConfirmationModal";
import blankProfile from "../../../img/hotgig-loading.png";
import EmojiPicker from "emoji-picker-react";
import { Storage } from "aws-amplify";

import "./ConversationDetail.css";

@inject("conversationStore", "profileStore")
@observer
class ConversationDetail extends Component {
    state = {
        participantArr: [],
        title: "",
        messageBody: "",
        editingMessageId: null,
        editingMessageBody: "",
        deleteModalOpen: false,
        deleteMessageId: null,
        isSending: false,
        emojiAnchorEl: null,
        attachedFile: null,
        attachmentPreview: null,
        isUploading: false
    };

    textareaRef = React.createRef();
    fileInputRef = React.createRef();
    _refreshRequestId = 0;
    _isMounted = false;
    messagesAreaRef = React.createRef();

    scrollToBottom = () => {
        if (this.messagesAreaRef.current) {
            const container = this.messagesAreaRef.current;
            container.scrollTop = container.scrollHeight;
        }
    };

    componentDidMount() {
        this._isMounted = true;
        if (this.props.conversationId) {
            this.loadConversation(this.props.conversationId);
        }
        if (this.props.isComposing && this.props.prePopulateRecipient) {
            this.prePopulateRecipient(this.props.prePopulateRecipient);
        }
    }

    componentWillUnmount() {
        this._isMounted = false;
        this._refreshRequestId++;
    }

    componentDidUpdate(prevProps) {
        if (this.props.conversationId !== prevProps.conversationId) {
            this._refreshRequestId++;
            if (this.state.attachmentPreview) {
                URL.revokeObjectURL(this.state.attachmentPreview);
            }
            this.setState({
                participantArr: [],
                title: "",
                messageBody: "",
                editingMessageId: null,
                editingMessageBody: "",
                emojiAnchorEl: null,
                attachedFile: null,
                attachmentPreview: null,
                isUploading: false,
                isSending: false,
                deleteModalOpen: false,
                deleteMessageId: null
            });
            this.props.conversationStore.setMessageChats([]);
            if (this.props.conversationId) {
                this.loadConversation(this.props.conversationId);
            }
        }

        if (this.props.isComposing && !prevProps.isComposing) {
            if (this.props.prePopulateRecipient) {
                this.prePopulateRecipient(this.props.prePopulateRecipient);
            } else {
                this.resetState();
            }
        }
    }

    prePopulateRecipient = (profile) => {
        if (!profile || !profile.id) return this.resetState();
        const recipient = { ...profile };
        if (!recipient.fullName) {
            recipient.fullName = recipient.type === ProfileType.Agent.ordinal && recipient.profileName
                ? recipient.profileName
                : `${recipient.firstName || ""} ${recipient.lastName || ""}`.trim();
        }
        if (this.state.attachmentPreview) {
            URL.revokeObjectURL(this.state.attachmentPreview);
        }
        this.setState({
            participantArr: [recipient],
            title: "",
            messageBody: "",
            editingMessageId: null,
            editingMessageBody: "",
            deleteModalOpen: false,
            deleteMessageId: null,
            isSending: false,
            emojiAnchorEl: null,
            attachedFile: null,
            attachmentPreview: null,
            isUploading: false
        });
    };

    resetState = () => {
        if (this.state.attachmentPreview) {
            URL.revokeObjectURL(this.state.attachmentPreview);
        }
        this.setState({
            participantArr: [],
            title: "",
            messageBody: "",
            editingMessageId: null,
            editingMessageBody: "",
            emojiAnchorEl: null,
            attachedFile: null,
            attachmentPreview: null,
            isUploading: false
        });
        this.props.conversationStore.setMessageChats([]);
        this.props.conversationStore.setTitle("");
    };

    loadConversation = (id) => {
        const { currentProfile } = this.props;
        this.props.conversationStore.loadById(id).then(response => {
            if (response) {
                this.props.conversationStore.setTitle(response.subject);
                const participants = [];
                for (let i = 0; i < response.conversationParticipants.length; i++) {
                    if (response.conversationParticipants[i].profile.id !== currentProfile?.id) {
                        const profile = response.conversationParticipants[i].profile;
                        profile.fullName = profile.type === ProfileType.Agent.ordinal && profile.profileName
                            ? profile.profileName
                            : `${profile.firstName} ${profile.lastName}`;
                        participants.push(profile);
                    }
                }
                this.setState({ participantArr: participants, title: response.subject });
                this.refreshAttachmentUrls();
            }
        }).then(() => {
            requestAnimationFrame(() => {
                this.scrollToBottom();
            });
        });
    };

    refreshAttachmentUrls = async () => {
        const requestId = ++this._refreshRequestId;
        const { messageChats } = this.props.conversationStore;
        if (!messageChats || messageChats.length === 0) return;

        const updatedMessages = [...messageChats];
        let hasChanges = false;
        const parser = new DOMParser();

        const refreshPromises = updatedMessages.map(async (msg, i) => {
            if (!msg.body || !msg.body.includes("data-attachment-key")) return;

            try {
                const doc = parser.parseFromString(msg.body, "text/html");
                const attachments = doc.querySelectorAll(".msg-attachment[data-attachment-key]");
                if (attachments.length === 0) return;

                const urlPromises = Array.from(attachments).map(async (el) => {
                    const key = el.getAttribute("data-attachment-key");
                    if (!key) return;
                    const freshUrl = await Storage.get(key);
                    el.querySelectorAll("a").forEach(link => link.setAttribute("href", freshUrl));
                    el.querySelectorAll("img").forEach(img => img.setAttribute("src", freshUrl));
                });

                await Promise.all(urlPromises);
                updatedMessages[i] = { ...msg, body: doc.body.innerHTML };
                hasChanges = true;
            } catch (err) {
                console.warn("Failed to refresh attachment URL for message", msg.id, err);
            }
        });

        await Promise.all(refreshPromises);

        if (requestId !== this._refreshRequestId || !this._isMounted) return;

        if (hasChanges) {
            this.props.conversationStore.setMessageChats(updatedMessages);
        }
    };

    handleTitleChange = (e) => {
        this.setState({ title: e.target.value });
        this.props.conversationStore.setTitle(e.target.value);
    };

    handleMessageChange = (e) => {
        const value = e.target.value;
        const target = e.target;

        this.setState({ messageBody: value });
        this.props.conversationStore.setMessage(`<p>${value}</p>`);
        this.autoResizeTextarea(target);
        requestAnimationFrame(() => {
            if (target) {
                target.scrollTop = target.scrollHeight;
            }
        });
    };

    handleEditMessageChange = (e) => {
        const value = e.target.value;
        const target = e.target;

        this.setState({ editingMessageBody: value }, () => {
            target.scrollTop = target.scrollHeight;
        });

        this.autoResizeTextarea(target);
    };

    autoResizeTextarea = (textarea) => {
        if (textarea) {
            textarea.style.height = 'auto';
            textarea.style.height = Math.min(textarea.scrollHeight, 120) + 'px';
        }
    };

    handleKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            this.handleSendMessage();
        }
    };

    handleEditKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            this.handleSaveEdit();
        }
    };

    handleEmojiClick = (e) => {
        this.setState({ emojiAnchorEl: e.currentTarget });
    };

    handleEmojiClose = () => {
        this.setState({ emojiAnchorEl: null });
    };

    handleEmojiSelect = (emojiData) => {
        const { editingMessageId, editingMessageBody, messageBody } = this.state;
        const isEditing = Boolean(editingMessageId);
        const currentText = isEditing ? editingMessageBody : messageBody;
        const emoji = emojiData.emoji;
        const textarea = this.textareaRef.current;

        let newBody;
        let cursorPos;

        if (textarea && typeof this.lastCursorPosition === 'number') {
            const start = this.lastCursorPosition;
            newBody = currentText.slice(0, start) + emoji + currentText.slice(start);
            cursorPos = start + emoji.length;
        } else {
            newBody = currentText + emoji;
            cursorPos = newBody.length;
        }

        if (isEditing) {
            this.setState({
                editingMessageBody: newBody,
                emojiAnchorEl: null
            }, () => {
                if (textarea) {
                    textarea.focus();
                    textarea.setSelectionRange(cursorPos, cursorPos);
                }
            });
        } else {
            this.setState({
                messageBody: newBody,
                emojiAnchorEl: null
            }, () => {
                if (textarea) {
                    textarea.focus();
                    textarea.setSelectionRange(cursorPos, cursorPos);
                }
            });
            this.props.conversationStore.setMessage(`<p>${newBody}</p>`);
        }
    };

    handleTextareaBlur = () => {
        if (this.textareaRef.current) {
            this.lastCursorPosition = this.textareaRef.current.selectionStart;
        }
    };

    handleAttachClick = () => {
        if (this.fileInputRef.current) {
            this.fileInputRef.current.click();
        }
    };

    handleFileSelect = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const maxSize = 10 * 1024 * 1024;
        if (file.size > maxSize) {
            alert("File size must be under 10MB");
            return;
        }

        let preview = null;
        if (file.type.startsWith("image/")) {
            preview = URL.createObjectURL(file);
        }

        this.setState({ attachedFile: file, attachmentPreview: preview });

        if (this.fileInputRef.current) {
            this.fileInputRef.current.value = "";
        }
    };

    handleRemoveAttachment = () => {
        if (this.state.attachmentPreview) {
            URL.revokeObjectURL(this.state.attachmentPreview);
        }
        this.setState({ attachedFile: null, attachmentPreview: null });
    };

    uploadAttachment = async (file) => {
        const { currentProfile } = this.props;
        const profileId = currentProfile?.id || "unknown";
        const timestamp = Date.now();
        const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, "_");
        const storagePath = `message/attachments/${profileId}/${timestamp}_${safeName}`;

        await Storage.put(storagePath, file, {
            contentType: file.type
        });

        const url = await Storage.get(storagePath);
        return { url, key: storagePath, name: file.name, type: file.type, size: file.size };
    };

    formatFileSize = (bytes) => {
        if (bytes < 1024) return bytes + " B";
        if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
        return (bytes / (1024 * 1024)).toFixed(1) + " MB";
    };

    escapeHtml = (str) => {
        return String(str)
            .replace(/&/g, "&amp;")
            .replace(/</g, "&lt;")
            .replace(/>/g, "&gt;")
            .replace(/"/g, "&quot;")
            .replace(/'/g, "&#039;");
    };

    buildAttachmentHtml = (attachment) => {
        const safeName = this.escapeHtml(attachment.name);
        const safeType = this.escapeHtml(attachment.type);
        const safeUrl = encodeURI(attachment.url);
        const safeKey = this.escapeHtml(attachment.key);
        const isImage = attachment.type.startsWith("image/");
        if (isImage) {
            return `<div class="msg-attachment" data-attachment-name="${safeName}" data-attachment-type="${safeType}" data-attachment-size="${attachment.size}" data-attachment-key="${safeKey}"><a href="${safeUrl}" target="_blank" rel="noopener noreferrer"><img src="${safeUrl}" alt="${safeName}" style="max-width:300px;max-height:200px;border-radius:8px;" /></a><div class="msg-attachment-info">${safeName} (${this.formatFileSize(attachment.size)})</div></div>`;
        }
        return `<div class="msg-attachment" data-attachment-name="${safeName}" data-attachment-type="${safeType}" data-attachment-size="${attachment.size}" data-attachment-key="${safeKey}"><a href="${safeUrl}" target="_blank" rel="noopener noreferrer" class="msg-attachment-link">${safeName} (${this.formatFileSize(attachment.size)})</a></div>`;
    };

    handleAddParticipant = (participant) => {
        if (!participant.fullName?.includes("No User Found")) {
            if (participant.type === ProfileType.Agent.ordinal) {
                participant.fullName = participant.profileName;
            }
            this.setState(prev => ({
                participantArr: [...prev.participantArr, participant]
            }));
        }
    };

    handleRemoveParticipant = (participantId) => {
        this.setState(prev => ({
            participantArr: prev.participantArr.filter(p => p.id !== participantId)
        }));
    };

    handleSendMessage = async () => {
        const { conversationId, isComposing, onMessageSent } = this.props;
        const { title, participantArr, messageBody, attachedFile } = this.state;

        if (!messageBody.trim() && !attachedFile) return;

        this.setState({ isSending: true });

        try {
            let bodyHtml = messageBody.trim() ? `<p>${messageBody}</p>` : "";

            if (attachedFile) {
                this.setState({ isUploading: true });
                const attachment = await this.uploadAttachment(attachedFile);
                bodyHtml += this.buildAttachmentHtml(attachment);
                this.setState({ isUploading: false });
            }

            this.props.conversationStore.setMessage(bodyHtml);

            if (this.state.attachmentPreview) {
                URL.revokeObjectURL(this.state.attachmentPreview);
            }

            if (isComposing) {
                this.props.conversationStore.setParticipant(participantArr);
                this.props.conversationStore.setTitle(title);
                const response = await this.props.conversationStore.submit();
                this.setState({ messageBody: "", isSending: false, attachedFile: null, attachmentPreview: null, isUploading: false });
                if (this.fileInputRef.current) this.fileInputRef.current.value = "";
                if (this.textareaRef.current) this.textareaRef.current.style.height = 'auto';
                if (onMessageSent) onMessageSent(response.id);
            } else {
                await this.props.conversationStore.submitMessage();
                this.setState({ messageBody: "", isSending: false, attachedFile: null, attachmentPreview: null, isUploading: false });
                if (this.fileInputRef.current) this.fileInputRef.current.value = "";
                if (this.textareaRef.current) this.textareaRef.current.style.height = 'auto';
                this.loadConversation(conversationId);
            }
        } catch (error) {
            console.error("Message send error:", error);
            this.setState({ isSending: false, isUploading: false });
        }
    };

    handleEditMessage = (messageId, body) => {
        this.setState({
            editingMessageId: messageId,
            editingMessageBody: body.replace(/<[^>]*>/g, '')
        }, () => {
            if (this.textareaRef.current) {
                this.textareaRef.current.focus();
            }
        });
    };

    handleSaveEdit = () => {
        const { editingMessageId, editingMessageBody } = this.state;

        if (!editingMessageBody.trim()) return;

        this.props.conversationStore.updateMessage(editingMessageId, editingMessageBody)
            .then(() => {
                this.setState({ editingMessageId: null, editingMessageBody: "" });
            })
            .catch(err => {
                console.error("Failed to update message:", err);
            });
    };

    handleCancelEdit = () => {
        this.setState({ editingMessageId: null, editingMessageBody: "" });
    };

    handleOpenDeleteModal = (messageId) => {
        this.setState({ deleteModalOpen: true, deleteMessageId: messageId });
    };

    handleCloseDeleteModal = () => {
        this.setState({ deleteModalOpen: false, deleteMessageId: null });
    };

    handleDeleteMessage = async () => {
        const { deleteMessageId } = this.state;
        const { messageChats } = this.props.conversationStore;

        await this.props.conversationStore.deleteMessage(deleteMessageId);
        const filteredMessages = messageChats.filter(x => x.id !== deleteMessageId);
        this.props.conversationStore.setMessages(filteredMessages);
        this.handleCloseDeleteModal();
    };

    formatMessageTime = (date) => {
        return moment(date).format('h:mm A');
    };

    formatDateDivider = (date) => {
        const msgDate = moment(date);
        const today = moment().startOf('day');
        const yesterday = moment().subtract(1, 'day').startOf('day');

        if (msgDate.isSame(today, 'day')) return 'Today';
        if (msgDate.isSame(yesterday, 'day')) return 'Yesterday';
        return msgDate.format('MMMM D, YYYY');
    };

    shouldShowDateDivider = (messages, index) => {
        if (index === 0) return true;
        const currentDate = moment(messages[index].createdAt).startOf('day');
        const prevDate = moment(messages[index - 1].createdAt).startOf('day');
        return !currentDate.isSame(prevDate);
    };

    getInitials = (profile) => {
        if (!profile) return "?";
        const firstName = profile.firstName || "";
        const lastName = profile.lastName || "";
        return (firstName[0] || "") + (lastName[0] || "").toUpperCase();
    };

    renderEmptyState = () => (
        <div className="detail-empty-state">
            <div className="empty-icon">💬</div>
            <h3>Select a conversation</h3>
            <p>Choose a conversation from the list or start a new message.</p>
        </div>
    );

    renderComposer = (isComposeView = false) => {
        const { title, participantArr, messageBody, editingMessageId, editingMessageBody, isSending, emojiAnchorEl } = this.state;
        const isEditing = Boolean(editingMessageId);

        return (
            <div className="composer">
                {this.state.attachedFile && (
                    <div className="attachment-preview-bar">
                        {this.state.attachmentPreview ? (
                            <img src={this.state.attachmentPreview} alt="Preview" className="attachment-thumb" />
                        ) : (
                            <InsertDriveFileIcon className="attachment-file-icon" />
                        )}
                        <div className="attachment-info">
                            <span className="attachment-name">{this.state.attachedFile.name}</span>
                            <span className="attachment-size">{this.formatFileSize(this.state.attachedFile.size)}</span>
                        </div>
                        <IconButton size="small" onClick={this.handleRemoveAttachment} className="attachment-remove">
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </div>
                )}
                <div className="composer-tools">
                    <Tooltip title="Add emoji">
                        <IconButton size="small" onClick={this.handleEmojiClick}>
                            <InsertEmoticonIcon />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Attach file">
                        <IconButton size="small" onClick={this.handleAttachClick}>
                            <AttachFileIcon />
                        </IconButton>
                    </Tooltip>
                </div>

                <div className="composer-input-wrapper">
                    <textarea
                        ref={this.textareaRef}
                        className="composer-textarea"
                        placeholder={isEditing ? "Edit message..." : (isEditing ? "Edit message..." : "Type a message...")}
                        value={isEditing ? editingMessageBody : messageBody}
                        onChange={isEditing ? this.handleEditMessageChange : this.handleMessageChange}
                        onKeyDown={isEditing ? this.handleEditKeyDown : this.handleKeyDown}
                        onBlur={this.handleTextareaBlur}
                        rows={1}
                    />
                </div>
                
                {isEditing ? (
                    <div className="edit-actions" style={{ display: 'flex', gap: '8px' }}>
                        <Button
                            size="small"
                            onClick={this.handleCancelEdit}
                            className="edit-cancel"
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            color="primary"
                            size="small"
                            onClick={this.handleSaveEdit}
                            disabled={!editingMessageBody.trim()}
                            className="edit-save"
                        >
                            Save
                        </Button>
                    </div>
                ) : (
                    <Button
                        variant="contained"
                        className="send-button"
                        onClick={this.handleSendMessage}
                        disabled={isComposeView ? (isSending || !title || participantArr.length === 0 || (!messageBody.trim() && !this.state.attachedFile)) : (isSending || (!messageBody.trim() && !this.state.attachedFile))}
                    >
                        {isSending ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
                    </Button>
                )}
                <Popover
                    open={Boolean(emojiAnchorEl)}
                    anchorEl={emojiAnchorEl}
                    onClose={this.handleEmojiClose}
                    anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
                    transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                >
                    <EmojiPicker onEmojiClick={this.handleEmojiSelect} />
                </Popover>
            </div>
        );
    };

    renderComposeView = () => {
        const { onBackToList } = this.props;
        const { participantArr, title } = this.state;

        return (
            <Fragment>
                <div className="detail-header">
                    <div className="detail-header-left">
                        <IconButton className="back-button" onClick={onBackToList}>
                            <ArrowBackIcon />
                        </IconButton>
                        <span className="detail-title">New Message</span>
                    </div>
                </div>

                <div className="compose-form">
                    <div className="compose-field">
                        <label>To</label>
                        <div className="participant-input-container">
                            {participantArr.map(participant => (
                                <div key={participant.id} className="participant-tag">
                                    <span>{participant.fullName || `${participant.firstName} ${participant.lastName}`}</span>
                                    <button
                                        className="remove-tag"
                                        onClick={() => this.handleRemoveParticipant(participant.id)}
                                    >
                                        ×
                                    </button>
                                </div>
                            ))}
                            <ProfileSearchBox
                                height="auto"
                                id="search-profile"
                                disableInvite
                                placeholder="Add recipients..."
                                handleSearch={value => this.props.profileStore.searchAllProfiles(value)}
                                handleChange={this.handleAddParticipant}
                                exclude={participantArr}
                            />
                        </div>
                    </div>

                    <div className="compose-field">
                        <label>Subject</label>
                        <TextField
                            fullWidth
                            placeholder="Enter a subject..."
                            value={title}
                            onChange={this.handleTitleChange}
                            variant="outlined"
                            size="small"
                        />
                    </div>

                    <div className="compose-field compose-message">
                        <label>Message</label>
                        <input
                            type="file"
                            ref={this.fileInputRef}
                            style={{ display: "none" }}
                            onChange={this.handleFileSelect}
                            accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.zip"
                        />
                        {this.renderComposer(true)}
                    </div>
                </div>
            </Fragment>
        );
    };

    renderConversationView = () => {
        const { onBackToList, currentProfile } = this.props;
        const { messageChats, title } = this.props.conversationStore;
        const { participantArr, deleteModalOpen } = this.state;

        return (
            <Fragment>
                <div className="detail-header">
                    <div className="detail-header-left">
                        <IconButton className="back-button" onClick={onBackToList}>
                            <ArrowBackIcon />
                        </IconButton>
                        <div className="detail-header-info">
                            <span className="detail-title">{title || "Conversation"}</span>
                            <div className="detail-participants">
                                {participantArr.slice(0, 3).map(p => (
                                    <span key={p.id} className="participant-chip">
                                        {p.fullName || `${p.firstName} ${p.lastName}`}
                                    </span>
                                ))}
                                {participantArr.length > 3 && (
                                    <span className="participant-chip">+{participantArr.length - 3}</span>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                <div className="messages-area" ref={this.messagesAreaRef}>
                    {messageChats && messageChats.map((message, index) => {
                        const isMe = message.profileId === currentProfile?.id;
                        const showDateDivider = this.shouldShowDateDivider(messageChats, index);

                        return (
                            <Fragment key={message.id || index}>
                                {showDateDivider && (
                                    <div className="date-divider">
                                        <span>{this.formatDateDivider(message.createdAt)}</span>
                                    </div>
                                )}

                                {message.profileId ? (
                                    <div className={`message-group ${isMe ? 'me' : ''}`}>
                                        <div className="message-row">
                                            <Avatar
                                                src={message.profileImageUrl || blankProfile}
                                                className="message-avatar"
                                            >
                                                {this.getInitials(message.profile)}
                                            </Avatar>

                                            <div className="message-bubble">
                                                {isMe && (
                                                    <div className="message-actions">
                                                        <Tooltip title="Edit">
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => this.handleEditMessage(message.id, message.body)}
                                                            >
                                                                <EditIcon fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                        <Tooltip title="Delete">
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => this.handleOpenDeleteModal(message.id)}
                                                            >
                                                                <DeleteIcon fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </div>
                                                )}
                                                <div className="message-sender" style={{ color: isMe ? '#ffffff' : undefined }}>
                                                    {message.profile?.fullName || `${message.profile?.firstName || ''} ${message.profile?.lastName || ''}`}
                                                </div>
                                                <div
                                                    className="message-content"
                                                    dangerouslySetInnerHTML={{ __html: message.body }}
                                                />
                                                <div className="message-meta">
                                                    <span className="message-time">{this.formatMessageTime(message.createdAt)}</span>
                                                    {message.updatedAt && <span className="message-time"> Edited</span>}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ) : null}
                            </Fragment>
                        );
                    })}
                </div>

                <input
                    type="file"
                    ref={this.fileInputRef}
                    style={{ display: "none" }}
                    onChange={this.handleFileSelect}
                    accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.zip"
                />

                {this.renderComposer(false)}

                <ConfirmationModal
                    open={this.state.deleteModalOpen}
                    title="Delete Message"
                    message="Are you sure you want to delete this message?"
                    closeHandler={this.handleCloseDeleteModal}
                    confirmationHandler={this.handleDeleteMessage}
                    declineHandler={this.handleCloseDeleteModal}
                    confirmationLabel="Delete"
                />
            </Fragment>
        );
    };

    render() {
        const { conversationId, isComposing } = this.props;

        if (isComposing) {
            return <div className="conversation-detail">{this.renderComposeView()}</div>;
        }

        if (!conversationId) {
            return <div className="conversation-detail">{this.renderEmptyState()}</div>;
        }

        return <div className="conversation-detail">{this.renderConversationView()}</div>;
    }
}

export default ConversationDetail;